import  { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchAgents, fetchAgentStatuses, fetchCallStatuses, updateAgentStatus } from '../../services/agentService';
import AgentCard from '../AgentCard/AgentCard';
import BatchCallPanel from '../BatchCallPanel/BatchCallPanel';
import Toast from '../Toast/Toast';
import UserManagement from '../UserManagement/UserManagement';
import './Dashboard.css';
import { 
  Filter, 
  X, 
  Home, 
  Check, 
  Search, 
  Loader2, 
  RefreshCw, 
  AlertCircle,
  LogOut,
  Phone,
  Users,
  PhoneForwarded,
  Trash2,
  Send,
  ThumbsUp,
  ThumbsDown,
  BarChart3
} from 'lucide-react';



// Loading Component
const LoadingState = () => (
  <div className="loading-state">
    <div className="loading-spinner">
      <Loader2 />
    </div>
    <h3>Loading agents...</h3>
    <p>Connecting to database</p>
  </div>
);

// Error Component
const ErrorState = ({ error, onRetry }) => (
  <div className="error-state">
    <span className="error-icon-wrapper">
      <AlertCircle className="error-icon" />
    </span>
    <h3>Failed to load data</h3>
    <p>{error}</p>
    <button className="retry-btn" onClick={onRetry}>
      <span className="retry-icon-wrapper">
        <RefreshCw className="retry-icon" />
      </span>
      <span>Retry</span>
    </button>
  </div>
);

// Main Dashboard Component
const Dashboard = ({ user, onLogout, onOpenPricePredictions }) => {
  const [currentView, setCurrentView] = useState('agents'); // 'agents' or 'users'
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);
  const [totalProperties, setTotalProperties] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ lead: 0, prospect: 0, interested: 0, 'not-interested': 0, done: 0, negotiation: 0, lost: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    officeName: '',
    leadStatus: '',
    website: '',
    city: '',
    district: '',
    neighbourhood: '',
    phoneNumber: '',
    hasCallStatus: false
  });
  const [callingPhoneId, setCallingPhoneId] = useState(null);
  const [toast, setToast] = useState(null);
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [debouncedPhoneFilter, setDebouncedPhoneFilter] = useState('');
  const filtersRef = useRef(filters);
  const debouncedNameFilterRef = useRef(debouncedNameFilter);
  const debouncedPhoneFilterRef = useRef(debouncedPhoneFilter);

  // Batch phone selection: Set of "agentId::phoneNumber" keys
  const [selectedPhones, setSelectedPhones] = useState(new Set());
  // Map of agentId::phoneNumber -> { agentName, phoneNumber, agentId }
  const [selectedPhoneDetails, setSelectedPhoneDetails] = useState(new Map());
  // Track call statuses per phone number from batch calls
  const [phoneCallStatuses, setPhoneCallStatuses] = useState({});
  // Track call_successful per phone number (success | failure | unknown)
  const [phoneCallSuccessful, setPhoneCallSuccessful] = useState({});

  // Track call timestamps (created_at, updated_at) per phone number
  const [phoneCallTimestamps, setPhoneCallTimestamps] = useState({});
  // Batch call name input
  const [batchCallName, setBatchCallName] = useState('');
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [showBatchConfirm, setShowBatchConfirm] = useState(false);
  // Ref for batch panel to refresh
  const batchPanelRef = useRef(null);

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Keep filtersRef in sync
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Keep debouncedNameFilterRef in sync
  useEffect(() => {
    debouncedNameFilterRef.current = debouncedNameFilter;
  }, [debouncedNameFilter]);

  // Keep debouncedPhoneFilterRef in sync
  useEffect(() => {
    debouncedPhoneFilterRef.current = debouncedPhoneFilter;
  }, [debouncedPhoneFilter]);

  // Debounce for name filter - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(filters.name);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.name]);

  // Debounce for phone filter - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPhoneFilter(filters.phoneNumber);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.phoneNumber]);

  // Only reload data when debounced name filter changes
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
      const currentFilters = { ...filtersRef.current, name: debouncedNameFilter, phoneNumber: debouncedPhoneFilter };
      loadData(1, currentFilters);
    }
  }, [debouncedNameFilter]);

  // Only reload data when debounced phone filter changes
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
      const currentFilters = { ...filtersRef.current, name: debouncedNameFilter, phoneNumber: debouncedPhoneFilter };
      loadData(1, currentFilters);
    }
  }, [debouncedPhoneFilter]);

  // Fetch data from API
  const loadData = useCallback(async (page = 1, filterOverrides = null) => {
    setLoading(true);
    setError(null);
    try {
      // When no filterOverrides, use filtersRef but with debounced name and phone
      const filtersToUse = filterOverrides || { ...filtersRef.current, name: debouncedNameFilterRef.current, phoneNumber: debouncedPhoneFilterRef.current };
      const [agentsResponse, statusesResponse, callStatusesResponse] = await Promise.all([
        fetchAgents(page, 50, filtersToUse),
        fetchAgentStatuses(),
        fetchCallStatuses()
      ]);
      

      
      if (agentsResponse.success) {
        // Merge agent data with statuses
        const mergedAgents = agentsResponse.data.map(agent => ({
          ...agent,
          ...(statusesResponse.data?.[agent.id] || {})
        }));
        
        setAgents(mergedAgents);
        setCurrentPage(agentsResponse.currentPage);
        setTotalPages(agentsResponse.totalPages);
        setTotalAgents(agentsResponse.totalAgents);
        setTotalProperties(agentsResponse.totalProperties);
        setHasMore(agentsResponse.hasMore);
        if (agentsResponse.statusCounts) {
          setStatusCounts(agentsResponse.statusCounts);
        }

        // Merge call statuses from DB
        if (callStatusesResponse.success && callStatusesResponse.data) {
          setPhoneCallStatuses(prev => ({
            ...prev,
            ...callStatusesResponse.data
          }));
          if (callStatusesResponse.callSuccessful) {
            setPhoneCallSuccessful(prev => ({
              ...prev,
              ...callStatusesResponse.callSuccessful
            }));
          }
          if (callStatusesResponse.timestamps) {
            setPhoneCallTimestamps(prev => ({
              ...prev,
              ...callStatusesResponse.timestamps
            }));
          }
        }
      } else {
        throw new Error('Failed to fetch agents');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to server. Make sure the backend is running on port 5000.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    setIsInitialLoad(false);
  }, []);

  // Get filter options dynamically from loaded agents
  const filterOptions = useMemo(() => {
    const cities = new Set();
    const districts = new Set();
    const neighbourhoods = new Set();
    const officeNames = new Set();
    const websites = new Set();
    
    agents.forEach(agent => {
      if (agent.officeName) officeNames.add(agent.officeName);
      agent.properties.forEach(prop => {
        if (prop.city) cities.add(prop.city);
        if (prop.district) districts.add(prop.district);
        if (prop.neighbourhood) neighbourhoods.add(prop.neighbourhood);
        if (prop.website) websites.add(prop.website);
      });
    });
    
    return {
      officeNames: Array.from(officeNames).sort(),
      websites: Array.from(websites).sort(),
      cities: Array.from(cities).sort(),
      districts: Array.from(districts).filter(d => 
        !filters.city || agents.some(a => 
          a.properties.some(p => p.city === filters.city && p.district === d)
        )
      ).sort(),
      neighbourhoods: Array.from(neighbourhoods).filter(n =>
        (!filters.city && !filters.district) || agents.some(a =>
          a.properties.some(p => 
            (!filters.city || p.city === filters.city) &&
            (!filters.district || p.district === filters.district) &&
            p.neighbourhood === n
          )
        )
      ).sort()
    };
  }, [agents, filters.city, filters.district]);

  // No frontend filtering needed - all handled by backend
  const filteredAgents = agents;

  // Stats - use total counts from backend (across ALL agents, not just current page)
  const stats = useMemo(() => {
    const leads = statusCounts.lead;
    const prospects = statusCounts.prospect;
    const interested = statusCounts.interested;
    const notInterested = statusCounts['not-interested'];
    const done = statusCounts.done;
    
    return { 
      totalAgents, 
      totalProperties, 
      leads, 
      prospects, 
      interested,
      notInterested,
      done
    };
  }, [statusCounts, totalAgents, totalProperties]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Reset dependent filters
      if (field === 'city') {
        newFilters.district = '';
        newFilters.neighbourhood = '';
      }
      if (field === 'district') {
        newFilters.neighbourhood = '';
      }
      
      // For name and phone fields, don't immediately load - let debounce handle it
      if (field !== 'name' && field !== 'phoneNumber') {
        // Reset to page 1 and reload with new filters
        // Use debounced values instead of raw to avoid race condition
        setCurrentPage(1);
        const filtersWithDebounced = { ...newFilters, name: debouncedNameFilter, phoneNumber: debouncedPhoneFilter };
        loadData(1, filtersWithDebounced);
      }
      
      return newFilters;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      name: '',
      officeName: '',
      leadStatus: '',
      website: '',
      city: '',
      district: '',
      neighbourhood: '',
      phoneNumber: '',
      hasCallStatus: false
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadData(1, clearedFilters);
  };

  // Toggle phone selection for batch
  const handleTogglePhone = (agent, phone) => {
    const key = `${agent.id}::${phone.number}`;
    setSelectedPhones(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setSelectedPhoneDetails(prev => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, { agentId: agent.id, agentName: agent.name, phoneNumber: phone.number });
      }
      return next;
    });
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedPhones(new Set());
    setSelectedPhoneDetails(new Map());
  };

  // Submit selected phones as batch call
  const handleSubmitBatch = async () => {
    if (selectedPhones.size === 0) return;
    const name = batchCallName.trim() || `Batch ${new Date().toLocaleString()}`;
    setSubmittingBatch(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const recipients = Array.from(selectedPhoneDetails.values()).map(d => ({
        phone_number: d.phoneNumber
      }));
      const response = await fetch(`${API_BASE}/elevenlabs/batch-calls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callName: name, recipients })
      });
      const data = await response.json();
      if (data.success) {
        // Mark all selected as pending
        const newStatuses = { ...phoneCallStatuses };
        selectedPhoneDetails.forEach(d => { newStatuses[d.phoneNumber] = 'pending'; });
        setPhoneCallStatuses(newStatuses);
        setToast({ message: `Batch call "${name}" created with ${recipients.length} numbers`, type: 'success' });
        clearSelection();
        setBatchCallName('');
        // Refresh batch panel if viewing
        if (batchPanelRef.current?.refresh) batchPanelRef.current.refresh();
      } else {
        setToast({ message: data.error || 'Failed to create batch call', type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Error creating batch call: ' + err.message, type: 'error' });
    } finally {
      setSubmittingBatch(false);
    }
  };

  // Poll ElevenLabs conversation status for a single WhatsApp call
  // Stops when terminal status reached (done / failed / cancelled) or after 5 minutes
  // Track last update time per phone to prevent race conditions
  const [lastStatusUpdate, setLastStatusUpdate] = useState({});

  const pollConversationStatus = useCallback((phoneNumber, conversationId) => {
    const API_BASE = process.env.REACT_APP_API_URL || '/api';
    const terminalStatuses = new Set(['done', 'failed', 'cancelled']);
    const POLL_INTERVAL = 5000; // 5 seconds
    const TIMEOUT = 5 * 60 * 1000; // 5 minutes

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/elevenlabs/conversations/${conversationId}/status`);
        const data = await res.json();
        if (data.success) {
          // Only accept this response if it's newer than the last one (prevent race conditions)
          const lastUpdate = lastStatusUpdate[phoneNumber] || 0;
          if (data.timestamp > lastUpdate) {
            setPhoneCallStatuses(prev => ({ ...prev, [phoneNumber]: data.status }));
            setLastStatusUpdate(prev => ({ ...prev, [phoneNumber]: data.timestamp }));
            // Update call_successful if present
            if (data.call_successful) {
              setPhoneCallSuccessful(prev => ({ ...prev, [phoneNumber]: data.call_successful }));
            }
          }
          // Check raw ElevenLabs base status for terminal detection
          const baseStatus = data.status.split('-')[0]; // "done", "failed" etc.
          const isTerminalStatus = terminalStatuses.has(baseStatus);
          const isTerminalCallSuccessful = !!data.call_successful; // any non-empty value = terminal
          if (isTerminalStatus || isTerminalCallSuccessful) {
            clearInterval(interval);
          }
        } else {
          // API error — stop polling
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(interval);
    }, TIMEOUT);
  }, []);

  // Handle WhatsApp call
  const handleCall = async (agent, phone) => {
    setCallingPhoneId(phone.id);
    // Show 'pending' immediately while we wait for the API
    setPhoneCallStatuses(prev => ({ ...prev, [phone.number]: 'pending' }));
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_BASE}/elevenlabs/whatsapp-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: phone.number,
          dynamicVariables: {
            phone_number: phone.number,
            agent_name: agent.name
          }
        })
      });
      const data = await response.json();
      if (data.success) {
        const conversationId = data.data?.conversation_id;
        if (conversationId) {
          // Start polling real status (initiated → in-progress → processing → done/failed)
          pollConversationStatus(phone.number, conversationId);
        } else {
          // No conversation ID — can't poll, just leave as pending
        }
        setToast({ message: `WhatsApp permission request sent to ${phone.number}`, type: 'success' });
      } else {
        setPhoneCallStatuses(prev => ({ ...prev, [phone.number]: 'failed' }));
        setToast({ message: data.error || 'Failed to initiate WhatsApp call', type: 'error' });
      }
    } catch (err) {
      setPhoneCallStatuses(prev => ({ ...prev, [phone.number]: 'failed' }));
      setToast({ message: 'Failed to initiate WhatsApp call: ' + err.message, type: 'error' });
    } finally {
      setCallingPhoneId(null);
    }
  };

  // Handle status update
  const handleUpdateStatus = async (agentId, status, notes, skipValidation = false) => {
    const agent = agents.find(a => a.id === agentId);
    const notesToSave = notes !== undefined ? notes : agent?.notes || '';
    
    // Validation: both status and notes are required (unless skipping validation for reset)
    if (!skipValidation && (!status || !notesToSave.trim())) {
      setToast({ message: 'Please add both status and notes before saving', type: 'error' });
      return;
    }
    
    try {
      await updateAgentStatus(agentId, {
        leadStatus: status,
        notes: notesToSave
      });
      
      // If resetting (both status and notes are null/empty), clear lastUpdatedAt
      const isResetting = !status && !notesToSave;
      
      setAgents(prev => prev.map(a =>
        a.id === agentId ? { 
          ...a, 
          leadStatus: status, 
          notes: notesToSave,
          lastUpdatedAt: isResetting ? null : new Date().toISOString()
        } : a
      ));
      const message = isResetting ? 'Agent data reset successfully!' : 'Status updated successfully!';
      setToast({ message: message, type: 'success' });
    } catch (err) {
      const errorMessage = err.message || 'Failed to update status';
      setToast({ message: errorMessage, type: 'error' });
    }
  };

  // Handle save notes
  const handleSaveNotes = async (agentId, notes) => {
    const agent = agents.find(a => a.id === agentId);
    try {
      await updateAgentStatus(agentId, {
        leadStatus: agent?.leadStatus,
        notes: notes
      });
      
      setAgents(prev => prev.map(a =>
        a.id === agentId ? { ...a, notes } : a
      ));
    } catch (err) {
      setToast({ message: 'Failed to save notes', type: 'error' });
    }
  };

  if (loading && isRefreshing) {
    return (
      <div className="sales-dashboard">
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-title">
              <img src="/reos_logo.png" alt="Sales Dashboard" className="header-logo" />
              <h1>Sales Dashboard</h1>
            </div>
          </div>
        </header>
        <main className="dashboard-content">
          <LoadingState />
        </main>
      </div>
    );
  }

  if (error && isInitialLoad) {
    return (
      <div className="sales-dashboard">
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-title">
              <img src="/reos_logo.png" alt="Sales Dashboard" className="header-logo" />
              <h1>Sales Dashboard</h1>
            </div>
          </div>
        </header>
        <main className="dashboard-content">
          <ErrorState error={error} onRetry={loadData} />
        </main>
      </div>
    );
  }

  return (
    <div className="sales-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-top">
            <div className="header-title">
              <img src="/reos_logo.png" alt="Sales Dashboard" className="header-logo" />
              <h1>Sales Dashboard</h1>
            </div>
            <div className="header-actions">
              {user && user.role === 'admin' && (
                <button 
                  className={`view-btn ${currentView === 'users' ? 'active' : ''}`}
                  onClick={() => setCurrentView(currentView === 'agents' ? 'users' : 'agents')}
                  title={currentView === 'agents' ? 'Manage Users' : 'View Agents'}
                >
                  {currentView === 'agents' ? (
                    <>
                      <Users />
                      <span>Users</span>
                    </>
                  ) : (
                    <>
                      <Home />
                      <span>Agents</span>
                    </>
                  )}
                </button>
              )}
              <button 
                className="view-btn batch-calls-btn"
                onClick={() => setCurrentView(currentView === 'batchCalls' ? 'agents' : 'batchCalls')}
                title="Batch Calls"
              >
                <PhoneForwarded />
                <span>Batch Calls</span>
              </button>
              {onOpenPricePredictions && (
                <button
                  className="view-btn ai-predictions-btn"
                  onClick={onOpenPricePredictions}
                  title="AI Price Predictions"
                >
                  <BarChart3 />
                  <span>AI Predictions</span>
                </button>
              )}
              <button className="refresh-btn" onClick={() => { setIsRefreshing(true); loadData(currentPage, { ...filtersRef.current, name: debouncedNameFilter, phoneNumber: debouncedPhoneFilter }); }} title="Refresh data">
                <RefreshCw />
                <span>Refresh</span>
              </button>
              {onLogout && (
                <button className="logout-btn" onClick={onLogout} title="Logout">
                  <LogOut />
                  <span>Logout</span>
                </button>
              )}
            </div>
          </div>
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-icon agents">
                < Search />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalAgents}</div>
                <div className="stat-label">Total Agents</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon properties">
                <Home />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalProperties}</div>
                <div className="stat-label">Properties</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon leads">
                <Phone />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.leads}</div>
                <div className="stat-label">Leads</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon prospects">
                <AlertCircle />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.prospects}</div>
                <div className="stat-label">Prospects</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon interested">
                <ThumbsUp />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.interested}</div>
                <div className="stat-label">Interested</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon not-interested">
                <ThumbsDown />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.notInterested}</div>
                <div className="stat-label">Not Interested</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon done">
                <Check />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.done}</div>
                <div className="stat-label">Done</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content">
        {currentView === 'users' ? (
          <UserManagement onShowToast={showToast} />
        ) : currentView === 'batchCalls' ? (
          <BatchCallPanel ref={batchPanelRef} onShowToast={showToast} onStatusUpdate={setPhoneCallStatuses} callStatuses={phoneCallStatuses} />
        ) : (
          <>
        {/* Filters */}
        <section className="filters-section">
          <div className="filters-header">
            <h2>
              <Filter />
              Filters
            </h2>
            <button className="clear-filters-btn" onClick={clearFilters}>
              <X />
              Clear All
            </button>
          </div>
          <div className="filters-grid">
            <div className="filter-group">
              <label>City</label>
              <select
                className="filter-select"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              >
                <option value="">All Cities</option>
                {filterOptions.cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>District</label>
              <select
                className="filter-select"
                value={filters.district}
                onChange={(e) => handleFilterChange('district', e.target.value)}
              >
                <option value="">All Districts</option>
                {filterOptions.districts.map(district => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Neighbourhood</label>
              <select
                className="filter-select"
                value={filters.neighbourhood}
                onChange={(e) => handleFilterChange('neighbourhood', e.target.value)}
              >
                <option value="">All Neighbourhoods</option>
                {filterOptions.neighbourhoods.map(neighbourhood => (
                  <option key={neighbourhood} value={neighbourhood}>{neighbourhood}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Agent Name</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name..."
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Phone Number</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Search by phone..."
                value={filters.phoneNumber}
                onChange={(e) => handleFilterChange('phoneNumber', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Office</label>
              <select
                className="filter-select"
                value={filters.officeName}
                onChange={(e) => handleFilterChange('officeName', e.target.value)}
              >
                <option value="">All Offices</option>
                {filterOptions.officeNames.map(office => (
                  <option key={office} value={office}>{office}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Lead Status</label>
              <select
                className="filter-select"
                value={filters.leadStatus}
                onChange={(e) => handleFilterChange('leadStatus', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="none">No Status</option>
                <option value="lead">Lead</option>
                <option value="prospect">Prospect</option>
                <option value="interested">Interested</option>
                <option value="not-interested">Not Interested</option>
                <option value="negotiation">Negotiation</option>
                <option value="done">Done</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Portal</label>
              <select
                className="filter-select"
                value={filters.website}
                onChange={(e) => handleFilterChange('website', e.target.value)}
              >
                <option value="">All Portals</option>
                {filterOptions.websites.map(website => (
                  <option key={website} value={website}>{website}</option>
                ))}
              </select>
            </div>
            <div className="filter-group call-status-toggle-group">
              <label>Call Status</label>
              <label className="ios-switch">
                <input
                  type="checkbox"
                  checked={filters.hasCallStatus}
                  onChange={(e) => handleFilterChange('hasCallStatus', e.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </section>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className="pagination-btn"
              onClick={() => loadData(currentPage - 1, { ...filtersRef.current, name: debouncedNameFilter, phoneNumber: debouncedPhoneFilter })}
              disabled={currentPage === 1 || loading}
            >
              Previous
            </button>
            <div className="pagination-info">
              Page {currentPage} of {totalPages} (Showing {filteredAgents.length} of {totalAgents} agents)
            </div>
            <button 
              className="pagination-btn"
              onClick={() => loadData(currentPage + 1, { ...filtersRef.current, name: debouncedNameFilter, phoneNumber: debouncedPhoneFilter })}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        )}

        {/* Agents Grid */}
        {loading ? (
          <LoadingState />
        ) : filteredAgents.length > 0 ? (
          <div className="agents-grid">
            {filteredAgents.map(agent => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onCall={handleCall}
                onUpdateStatus={handleUpdateStatus}
                onSaveNotes={handleSaveNotes}
                callingPhoneId={callingPhoneId}
                selectedPhones={selectedPhones}
                onTogglePhone={handleTogglePhone}
                phoneCallStatuses={phoneCallStatuses}
                phoneCallSuccessful={phoneCallSuccessful}
                phoneCallTimestamps={phoneCallTimestamps}
              />
            ))}
          </div>
        ) : (
          <div className="no-results">
            <Search />
            <h3>No agents found</h3>
            <p>Try adjusting your filters to see more results</p>
          </div>
        )}
        </>
        )}
      </main>

      {/* Floating Batch Selection Bar */}
      {selectedPhones.size > 0 && (
        <div className="batch-selection-bar">
          <div className="batch-bar-info">
            <PhoneForwarded />
            <span className="batch-bar-count">{selectedPhones.size}</span>
            <span>phone{selectedPhones.size > 1 ? 's' : ''} selected</span>
          </div>
          <div className="batch-bar-selected-list">
            {Array.from(selectedPhoneDetails.values()).slice(0, 5).map((d, i) => (
              <span key={i} className="batch-bar-chip">
                {d.phoneNumber}
                <button onClick={() => handleTogglePhone({ id: d.agentId, name: d.agentName }, { number: d.phoneNumber })}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {selectedPhoneDetails.size > 5 && (
              <span className="batch-bar-chip more">+{selectedPhoneDetails.size - 5} more</span>
            )}
          </div>
          <div className="batch-bar-actions">
            <input
              type="text"
              className="batch-bar-name-input"
              placeholder="Batch name (optional)"
              value={batchCallName}
              onChange={(e) => setBatchCallName(e.target.value)}
            />
            <button className="batch-bar-btn clear" onClick={clearSelection}>
              <Trash2 size={16} />
              Clear
            </button>
            <button className="batch-bar-btn submit" onClick={() => setShowBatchConfirm(true)} disabled={submittingBatch}>
              {submittingBatch ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
              {submittingBatch ? 'Submitting...' : 'Send Batch Call'}
            </button>
          </div>
        </div>
      )}

      {/* Batch call confirmation modal */}
      {showBatchConfirm && (
        <div className="confirm-overlay" onClick={() => setShowBatchConfirm(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <p className="confirm-title">Send Batch Call?</p>
            <p className="confirm-subtitle">{selectedPhones.size} phone{selectedPhones.size > 1 ? 's' : ''} will be called{batchCallName.trim() ? ` — "${batchCallName.trim()}"` : ''}.</p>
            <div className="confirm-actions">
              <button className="confirm-btn cancel" onClick={() => setShowBatchConfirm(false)}>Cancel</button>
              <button
                className="confirm-btn ok"
                onClick={() => { setShowBatchConfirm(false); handleSubmitBatch(); }}
              >
                <PhoneForwarded size={14} /> Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;

